package com.routexk.quiz.pages

import com.routexk.quiz.ui.utils.rpx

import androidx.compose.runtime.*
import com.routexk.quiz.analytics.FbPixel
import com.routexk.quiz.analytics.Ga4
import com.routexk.quiz.di.LocalAppContainer
import com.routexk.quiz.funnel.FunnelApi
import com.routexk.quiz.funnel.FunnelCopy
import com.routexk.quiz.funnel.FunnelStore
import com.routexk.quiz.funnel.QuizDefinition
import com.routexk.quiz.funnel.QuizStepId
import com.routexk.quiz.funnel.ui.AnalysisRing
import com.routexk.quiz.funnel.ui.FunnelCard
import com.routexk.quiz.funnel.ui.FunnelCta
import com.routexk.quiz.funnel.ui.FunnelLayout
import com.routexk.quiz.funnel.ui.FunnelProgressBar
import com.routexk.quiz.funnel.ui.GoldStars
import com.routexk.quiz.funnel.ui.QuizOptionList
import com.routexk.quiz.funnel.ui.TestimonialCard
import com.routexk.quiz.funnel.ui.TrustBlock
import com.routexk.quiz.ui.components.forms.TextInput
import com.routexk.quiz.ui.theme.AppColors
import com.routexk.quiz.ui.theme.AppTypography
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*

@Page("/quiz")
@Composable
fun QuizPage() {
    val ctx = rememberPageContext()
    val container = LocalAppContainer.current
    val scope = rememberCoroutineScope()
    val mobile = com.routexk.quiz.ui.utils.isMobile()

    // A returning user who already finished the funnel (or paid) is starting
    // over — drop the old backend session so checkout gets a fresh Stripe
    // customer instead of the old one that already holds a subscription.
    remember {
        if (FunnelStore.paid || FunnelStore.answers.quizComplete) FunnelStore.startNewRun()
    }

    var answers by remember { mutableStateOf(FunnelStore.answers) }
    var stepIndex by remember { mutableStateOf(0) }
    var analyzing by remember { mutableStateOf(false) }
    var cityInput by remember { mutableStateOf(answers.city ?: FunnelStore.detectedCity ?: "") }
    var cityInputStarted by remember { mutableStateOf(false) }
    val quizStartMs = remember { kotlin.js.Date.now() }

    LaunchedEffect(Unit) {
        Ga4.screenView("quiz", 2)
        Ga4.event("quiz_started", "total_questions" to QuizDefinition.steps.size)
        FbPixel.pageView()
    }
    LaunchedEffect(stepIndex) {
        val s = QuizDefinition.steps[stepIndex]
        Ga4.event(
            "quiz_question_view",
            "question_number" to stepIndex + 1,
            "question_id" to s.id.gaId,
        )
        // The 7 questions share the /quiz URL — QuizStep is what tells them apart
        FbPixel.trackCustom(
            "QuizStep",
            "question_number" to stepIndex + 1,
            "question_id" to s.id.gaId,
        )
    }

    // Auto-fill the city from the visitor's IP (backend geo) when they haven't
    // typed one yet. The value stays fully editable.
    LaunchedEffect(Unit) {
        if (answers.city.isNullOrBlank()) {
            if (FunnelStore.detectedCity.isNullOrBlank() && FunnelStore.sessionUuid == null) {
                FunnelApi.ensureSession(container.defaultHttpClient)
            }
            FunnelStore.detectedCity?.takeIf { it.isNotBlank() }?.let { city ->
                if (cityInput.isBlank()) cityInput = city
                answers = answers.copy(city = city.trim())
            }
        }
    }

    val steps = QuizDefinition.steps
    val step = steps[stepIndex]
    // Selection is per-visit UI state: nothing is pre-selected, even when an
    // answer for this step is already stored
    var selectedIndex by remember(stepIndex) { mutableStateOf<Int?>(null) }
    var autoAdvancing by remember(stepIndex) { mutableStateOf(false) }

    fun persist() {
        FunnelStore.answers = answers
    }

    fun advance() {
        FunnelApi.let {
            scope.launch { it.trackEvent(container.defaultHttpClient, "quiz_step_${step.id.storageKey}") }
        }
        if (stepIndex < steps.lastIndex) {
            stepIndex++
        } else {
            val timeOnQuiz = ((kotlin.js.Date.now() - quizStartMs) / 1000).toInt()
            Ga4.event(
                "quiz_completed",
                "answers_count" to steps.size,
                "time_on_quiz_seconds" to timeOnQuiz,
            )
            FbPixel.trackCustom("QuizComplete", "time_on_quiz_seconds" to timeOnQuiz)
            persist()
            scope.launch { FunnelApi.saveQuizAnswers(container.defaultHttpClient, answers) }
            analyzing = true
        }
    }

    if (analyzing) {
        AnalysisScreen(onDone = { ctx.router.navigateTo("/results") })
        return
    }

    // Design: 560px column — progress bar, question card and options share this width
    FunnelLayout(title = com.routexk.quiz.i18n.t("RouteXK — Quick Quiz", "RouteXK — Cuestionario rápido"), fillViewport = true, maxContentWidth = 560.rpx) {
        // Back arrow only from step 2 on
        FunnelProgressBar(
            progress = (stepIndex + 1).toDouble() / (steps.size + 1),
            onBack = if (stepIndex > 0) ({
                Ga4.event(
                    "quiz_back_clicked",
                    "from_question_number" to stepIndex + 1,
                    "to_question_number" to stepIndex,
                )
                FbPixel.trackCustom(
                    "QuizBack",
                    "from_question" to stepIndex + 1,
                    "to_question" to stepIndex,
                )
                stepIndex--
            }) else null,
            modifier = Modifier.fillMaxWidth().margin(bottom = 32.rpx)
        )

        if (step.id == QuizStepId.CITY) {
            FunnelCard(Modifier.fillMaxWidth(), padding = 32.rpx, radius = 28.rpx) {
                Column(Modifier.fillMaxWidth().gap(24.rpx)) {
                    SpanText(
                        step.question,
                        Modifier
                            .fontSize(24.rpx)
                            .lineHeight(32.rpx)
                            .fontWeight(FontWeight.Bold)
                            .color(AppColors.Funnel.TextDark)
                    )
                    Column(Modifier.fillMaxWidth().gap(16.rpx)) {
                        TextInput(
                            value = cityInput,
                            onValueChange = {
                                if (!cityInputStarted) {
                                    cityInputStarted = true
                                    Ga4.event("city_input_started", "question_id" to step.id.gaId)
                                }
                                cityInput = it
                                answers = answers.copy(city = it.trim().ifEmpty { null })
                            },
                            placeholder = step.inputPlaceholder ?: "",
                            label = null
                        )
                        step.helper?.let {
                            Row(Modifier.fillMaxWidth().gap(8.rpx), verticalAlignment = Alignment.Top) {
                                SpanText(
                                    FunnelCopy.CITY_HELPER_EMOJI,
                                    Modifier.fontSize(14.rpx).lineHeight(16.rpx).flexShrink(0)
                                )
                                SpanText(
                                    it,
                                    Modifier.fontSize(14.rpx).lineHeight(18.rpx)
                                        .color(AppColors.Funnel.TextMuted)
                                )
                            }
                        }
                    }
                }
            }
        } else {
            SpanText(
                step.question,
                Modifier
                    .fillMaxWidth()
                    .fontSize(24.rpx)
                    .lineHeight(32.rpx)
                    .fontWeight(FontWeight.Bold)
                    .color(AppColors.Funnel.TextDark)
                    .margin(bottom = 20.rpx)
            )
            QuizOptionList(
                options = step.options.orEmpty(),
                selectedIndex = selectedIndex,
                onSelect = { index ->
                    if (!autoAdvancing) {
                        selectedIndex = index
                        answers = answers.withAnswer(step.id, index)
                        persist()
                        val gaParams = arrayOf(
                            "question_number" to (stepIndex + 1) as Any?,
                            "question_id" to step.id.gaId,
                            "answer_value" to step.gaAnswerValues?.getOrNull(index),
                        )
                        Ga4.event("quiz_answer_selected", *gaParams)
                        // Option steps have no Continue button — picking an
                        // option advances after a brief highlight; that tap is
                        // also the spec's quiz_continue_clicked
                        Ga4.event("quiz_continue_clicked", *gaParams)
                        FbPixel.trackCustom(
                            "QuizAnswer",
                            "question_number" to stepIndex + 1,
                            "question_id" to step.id.gaId,
                            "answer_value" to step.gaAnswerValues?.getOrNull(index),
                        )
                        autoAdvancing = true
                        scope.launch {
                            delay(250)
                            advance()
                        }
                    }
                }
            )
        }

        // Flexible spacer pins the trust block + CTA to the bottom of the viewport
        Box(Modifier.fillMaxWidth().flexGrow(1).minHeight(32.rpx))

        if (step.id == QuizStepId.CITY) {
            TrustBlock(Modifier.margin(bottom = 32.rpx))
        }

        // Option steps auto-advance on click, so the button only appears on
        // the city step, where the text input needs an explicit confirmation
        if (step.id == QuizStepId.CITY) {
            FunnelCta(
                text = if (mobile) FunnelCopy.CONFIRM else FunnelCopy.CONTINUE,
                enabled = cityInput.isNotBlank(),
                onClick = {
                    // Free-text city never goes into event params (PII rule);
                    // it is set as the user_city user property instead
                    Ga4.event(
                        "quiz_continue_clicked",
                        "question_number" to stepIndex + 1,
                        "question_id" to step.id.gaId,
                    )
                    // No answer_value for the city step: free-text city is PII
                    FbPixel.trackCustom(
                        "QuizAnswer",
                        "question_number" to stepIndex + 1,
                        "question_id" to step.id.gaId,
                    )
                    Ga4.setUserProperty("user_city", cityInput.trim().lowercase())
                    persist()
                    advance()
                }
            )
        }
    }
}

@Composable
private fun AnalysisScreen(onDone: () -> Unit) {
    var percent by remember { mutableStateOf(0) }
    val mobile = com.routexk.quiz.ui.utils.isMobile()

    LaunchedEffect(Unit) {
        Ga4.screenView("analyzing", 3)
        FbPixel.trackCustom("QuizAnalyzing")
        val startMs = kotlin.js.Date.now()
        // Always +1 per tick with an easing delay curve (~7s total) so the
        // counter never visibly jumps
        while (percent < 100) {
            val tick = when {
                percent < 40 -> 40L
                percent < 75 -> 70L
                else -> 120L
            }
            percent += 1
            delay(tick)
        }
        delay(600)
        Ga4.event(
            "analysis_completed",
            "duration_seconds" to ((kotlin.js.Date.now() - startMs) / 1000).toInt(),
        )
        onDone()
    }

    FunnelLayout(title = com.routexk.quiz.i18n.t("RouteXK — Analyzing", "RouteXK — Analizando"), maxContentWidth = 900.rpx, fillViewport = true) {
        Column(
            Modifier.fillMaxWidth().gap(24.rpx).flexGrow(1),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            FunnelCard(
                Modifier.margin(top = 16.rpx).maxWidth(560.rpx).fillMaxWidth(),
                padding = 24.rpx,
                radius = 32.rpx
            ) {
                Column(
                    Modifier.fillMaxWidth().gap(20.rpx),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    SpanText(
                        FunnelCopy.ANALYSIS_TITLE,
                        Modifier.fontSize(24.rpx).lineHeight(28.rpx).fontWeight(FontWeight.Bold)
                            .color(AppColors.Funnel.TextDark)
                    )
                    AnalysisRing(percent)
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        // Mobile: longer status lines wrap to two rows, so
                        // reserve two lines up front — otherwise the card
                        // height jumps as messages rotate
                        modifier = Modifier.gap(10.rpx)
                            .then(if (mobile) Modifier.height(40.rpx) else Modifier)
                    ) {
                        if (!mobile) {
                            // Small gold spinner (desktop design only)
                            Box(
                                Modifier
                                    .size(18.rpx)
                                    .flexShrink(0)
                                    .borderRadius(50.percent)
                                    .styleModifier {
                                        property("border", "3px solid rgba(236, 169, 0, 0.25)")
                                        property("border-top-color", "#ECA900")
                                        property("animation", "funnel-spin 0.9s linear infinite")
                                    }
                            )
                        }
                        // Status line advances with the progress: 8 messages, one per 12.5%
                        val steps = FunnelCopy.ANALYSIS_STEPS
                        SpanText(
                            steps[(percent * steps.size / 100).coerceAtMost(steps.lastIndex)],
                            Modifier.fontSize(14.rpx).lineHeight(20.rpx)
                                .textAlign(com.varabyte.kobweb.compose.css.TextAlign.Center)
                                .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xEC, 0xA9, 0x00))
                        )
                    }
                }
            }

            if (mobile) {
                // Vertically auto-scrolling testimonials, filling the space
                // below the loader card; doubled list + translateY(-50%) loop
                Box(
                    Modifier.fillMaxWidth().flexGrow(1).styleModifier {
                        property("overflow", "hidden")
                        // Cards fade out gradually as they approach the analysis card
                        val fade = "linear-gradient(to bottom, transparent 0, black 64px)"
                        property("-webkit-mask-image", fade)
                        property("mask-image", fade)
                    }
                ) {
                    Column(
                        Modifier.fillMaxWidth().styleModifier {
                            property("animation", "funnel-marquee-y 30s linear infinite")
                        }
                    ) {
                        repeat(2) {
                            FunnelCopy.TESTIMONIALS.forEach {
                                TestimonialCard(it, Modifier.fillMaxWidth().margin(bottom = 16.rpx))
                            }
                        }
                    }
                }
            } else {
                // Spacer pins the rating row + testimonial strip to the bottom
                Box(Modifier.fillMaxWidth().flexGrow(1))

                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    modifier = Modifier.gap(10.rpx).alignSelf(com.varabyte.kobweb.compose.css.AlignSelf.Center)
                ) {
                    // Trustpilot logo above the rating, matching the quiz's TrustBlock.
                    org.jetbrains.compose.web.dom.Img(
                        src = "/funnel/trustpilot.svg",
                        attrs = {
                            attr("alt", "Trustpilot")
                            style { width(130.rpx); height(32.rpx) }
                        }
                    )
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        modifier = Modifier.gap(12.rpx)
                    ) {
                        SpanText(
                            FunnelCopy.ANALYSIS_RATING,
                            Modifier.fontSize(18.rpx).lineHeight(20.rpx)
                                .fontFamily(AppTypography.TERTIARY_FONT_NAME, AppTypography.TERTIARY_FONT_FALLBACK)
                                .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x3E, 0x3F, 0x5B))
                        )
                        GoldStars(size = 20)
                    }
                }

                // Full-bleed, slowly auto-scrolling strip of testimonials; the
                // list is doubled and translated by -50% for a seamless loop
                Box(
                    Modifier.styleModifier {
                        property("width", "100vw")
                        property("overflow", "hidden")
                    }
                ) {
                    Row(
                        Modifier.styleModifier {
                            property("width", "max-content")
                            property("animation", "funnel-marquee 45s linear infinite")
                        }
                    ) {
                        repeat(2) {
                            FunnelCopy.TESTIMONIALS.forEach {
                                TestimonialCard(it, Modifier.width(400.rpx).flexShrink(0).margin(right = 16.rpx))
                            }
                        }
                    }
                }
            }
        }
    }
}
